import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAssessmentCriteria, {
  SaveCriteriaResult,
} from '@/data/hook/evaluation/useAssessmentCriteria';
import useAssessmentGradeValidate from '@/data/hook/evaluation/useAssessmentGradeValidate';
import useAssessmentItems from '@/data/hook/evaluation/useAssessmentItems';
import useTemplatePropagation from '@/data/hook/evaluation/useTemplatePropagation';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import {
  Assessment,
  AssessmentItem,
  AssessmentCriteria,
} from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getNextOrder } from '@/utils/list';

import AssessmentItemForm, {
  AssessmentItemFormFields,
} from './AssessmentItemForm';
import { formatList } from '@/utils/i18n/formatList';

type AssessmentItemFormServiceProps = {
  evaluation: Evaluation;
  assessment: Assessment;
  cacheManager: ListCacheManager<Evaluation>;
  item?: AssessmentItem;
  onClose: () => void;
  onSuccess?: () => void;
};

export type AssessmentItemFormHelpers = {
  applyCriterionSaved: (index: number, saved: AssessmentCriteria) => void;
};

export type SubmitArgs = {
  data: AssessmentItemFormFields;
  helpers: AssessmentItemFormHelpers;
};

type SubmitResult = {
  item: AssessmentItem;
  criteria: SaveCriteriaResult;
};

export default function AssessmentItemFormService({
  evaluation,
  assessment,
  cacheManager,
  item,
  onClose,
  onSuccess,
}: AssessmentItemFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const [workingItem, setWorkingItem] = useState<AssessmentItem | undefined>(
    item,
  );

  const { items, createItem, updateItem } = useAssessmentItems({
    evaluation,
    assessment,
    cacheManager,
  });

  const { saveCriteria } = useAssessmentCriteria({
    evaluation,
    assessment,
    cacheManager,
  });

  const { validateMaxGrade, available } = useAssessmentGradeValidate({
    assessment,
    items,
    currentItemId: workingItem?.id,
  });

  const { propagate } = useTemplatePropagation({ evaluation });

  const reportFailures = (failed: SaveCriteriaResult['failed']) => {
    if (failed.length === 0) return false;
    const orders = formatList(failed.map(f => f.order).sort((a, b) => a - b));
    alert.error(t('criteriaSaveFailed', { count: failed.length, orders }));
    return true;
  };

  const handleCreate = async ({ data }: SubmitArgs): Promise<SubmitResult> => {
    const newItem = await createItem({
      name: data.name,
      description: data.description,
      type: data.type,
      maxGrade: data.maxGrade,
      order: getNextOrder(items),
    });

    const result = await saveCriteria({
      itemId: newItem.id,
      existing: [],
      next: data.criteria,
    });

    if (!result.failed.length) {
      await propagate();
      alert.success(t('createAssessmentItemSuccess'));
    }

    return { item: newItem, criteria: result };
  };

  const handleUpdate = async ({ data }: SubmitArgs): Promise<SubmitResult> => {
    if (!workingItem) throw new Error('Item is required for update');

    const dataChanges = buildChangedObject(workingItem, {
      name: data.name,
      description: data.description,
      type: data.type,
      maxGrade: data.maxGrade,
    });

    let currentItem = workingItem;
    if (Object.keys(dataChanges).length > 0) {
      currentItem = await updateItem({
        itemId: workingItem.id,
        data: dataChanges,
      });
    }

    const result = await saveCriteria({
      itemId: currentItem.id,
      existing: currentItem.criteria ?? [],
      next: data.criteria,
    });

    if (!result.failed.length) {
      await propagate();
      alert.success(t('updateAssessmentItemSuccess'));
    }

    return { item: currentItem, criteria: result };
  };

  const handleSubmit = (args: SubmitArgs) =>
    workingItem ? handleUpdate(args) : handleCreate(args);

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onSuccess: ({ criteria, item }, { helpers: { applyCriterionSaved } }) => {
      setWorkingItem({ ...item, criteria: criteria.nextCriteria });
      criteria.success.forEach(({ index, saved }) =>
        applyCriterionSaved(index, saved),
      );
      const partial = reportFailures(criteria.failed);
      if (partial) return;
      onSuccess?.();
      onClose();
    },
  });

  return (
    <AssessmentItemForm
      item={workingItem}
      onSubmit={onSubmit}
      onCancel={onClose}
      isLoading={isPending}
      validateMaxGrade={validateMaxGrade}
      maxGradeAvailable={available}
    />
  );
}
